'use client'

/**
 * React client for the copied Losi engine in `lib/losi-engine`
 * (framework-free; kept in sync with apps/losi-studio/src/bot).
 */

import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent
} from 'react'
import { useTheme } from 'next-themes'

import { NOTIF_BLUE } from '@/lib/losi-engine/decor'
import { BotEngine, type BotFrame } from '@/lib/losi-engine/engine'
import {
  LOIS_SPARKLE_SM,
  blendUniverse,
  fadeUniverse,
  sampleUniverse,
  sampleUniverseLogo,
  sampleUniverseOuter,
  sampleUniverseResting,
  seatUniverseOnGaze,
  type UniverseDecor
} from '@/lib/losi-engine/losi'
import { DEMI_VIEWBOX, RAYON } from '@/lib/losi-engine/repere'
import {
  COLOR_BY_ID,
  DEFAULT_SHAPE,
  ENCRE,
  SHAPE_BY_ID,
  decorAccent,
  isClusterShape,
  mixHex,
  resolveShapeId
} from '@/lib/losi-engine/skins'
import { EXPRESSION_BY_ID, type ExpressionId } from '@/lib/losi-engine/expressions'
import {
  orbitUniverseFade,
  orbitUniverseSettle,
  type StateId
} from '@/lib/losi-engine/states'
import { cn } from '@/lib/utils'

const R = RAYON
const VB = DEMI_VIEWBOX
const LOGO_PLANETS = new Set<StateId>(['sleep', 'logo', 'logoOuter'])

export interface LosiAvatarProps {
  state: StateId
  size?: number
  shape?: string
  color?: string
  /** Resting catalogue expression — only applies on `baseFace` states. */
  expression?: ExpressionId | null
  className?: string
  style?: CSSProperties
  /** Override eye backing / particle fog; defaults from theme */
  paper?: string
  /**
   * Scale so the body diameter matches `size` (viewBox margin would otherwise
   * make the blob look smaller than a photo avatar of the same box).
   */
  fitBody?: boolean
  /**
   * When true, the eyes track the mouse pointer via the engine's `Look` input.
   * Only visible on faced states (`baseFace`); ignored under reduced motion.
   */
  followCursor?: boolean
  /**
   * While the pointer is over the mark, play this state instead of `state`
   * (icon hover — wobble / spin / jiggle / any catalogue loader).
   */
  hoverState?: StateId | null
  /** Freeze the engine at this time (progress / skeleton loaders). */
  frozenAt?: number | null
  /**
   * Paint the Aura Field as a glow on the silhouette edge (not a circular orb).
   * Driven by CSS vars on this host or an ancestor:
   * `--orb-level`, `--orb-color-from`, `--orb-color-to`, `--orb-bass|mid|treble`.
   */
  auraBorder?: boolean
  onClick?: (e: ReactMouseEvent<HTMLElement>) => void
  title?: string
}

function universeDecorFor(id: StateId, t: number): UniverseDecor | null {
  if (id === 'sleep') return sampleUniverseResting(t, R)
  if (id === 'logo') return sampleUniverseLogo(t, R)
  if (id === 'logoOuter') return sampleUniverseOuter(t, R)
  return sampleUniverse(t, R)
}

function cometTrailOn(f: BotFrame): boolean {
  return (
    f.arcs.some((a) => a.opacity > 0.05) ||
    f.dots.some((d) => d.opacity > 0.05 && d.d)
  )
}

function decorate(
  engine: BotEngine,
  f: BotFrame,
  t: number,
  shapeId: string
): BotFrame {
  if (shapeId !== 'etoile') return { ...f, universe: null }

  const prev = engine.prevState
  const k = engine.morphRatio(t)
  const logoHandoff =
    prev !== null && k < 1 && (LOGO_PLANETS.has(prev) || LOGO_PLANETS.has(engine.state))

  let universe: UniverseDecor | null = null
  if (logoHandoff) {
    const from = universeDecorFor(prev!, t)
    const to = f.showUniverse ? universeDecorFor(engine.state, t) : null
    if (from && to) universe = blendUniverse(from, to, k)
    else if (from) universe = fadeUniverse(from, 1 - k)
    else if (to) universe = fadeUniverse(to, k)
  } else if (f.showUniverse) {
    universe = universeDecorFor(engine.state, t)
    if (universe && engine.state === 'orbit') {
      const local = engine.age(t)
      const fade = orbitUniverseFade(local)
      universe =
        fade <= 0
          ? null
          : fadeUniverse(
              blendUniverse(universe, sampleUniverseResting(t, R), orbitUniverseSettle(local)),
              fade
            )
    }
  }

  if (universe && engine.state === 'comet' && cometTrailOn(f)) {
    universe = {
      ...universe,
      satellite: { ...universe.satellite, opacity: 0 },
      trails: []
    }
  }

  if (universe) {
    universe = seatUniverseOnGaze(universe, f.gaze, f.bodyOffset)
  }

  return { ...f, universe }
}

function readCssColor(varName: string, fallback: string) {
  if (typeof window === 'undefined') return fallback
  const value = getComputedStyle(document.documentElement).getPropertyValue(varName).trim()
  if (!value) return fallback
  if (value.startsWith('#') || value.startsWith('rgb') || value.startsWith('hsl')) return value
  return `hsl(${value})`
}

export function LosiAvatar({
  state,
  size = 45,
  shape = DEFAULT_SHAPE,
  color,
  expression = null,
  className,
  style,
  paper: paperProp,
  fitBody = false,
  followCursor = false,
  hoverState = null,
  frozenAt = null,
  auraBorder = false,
  onClick,
  title
}: LosiAvatarProps) {
  const reactId = useId().replace(/:/g, '')
  const maskId = `losi-mask-${reactId}`
  const sheenClipId = `losi-sheen-${reactId}`
  const sheenGradId = `losi-sheen-g-${reactId}`
  const planetRingFrontId = `losi-ring-front-${reactId}`
  const { resolvedTheme } = useTheme()
  const [mounted, setMounted] = useState(false)
  const [hovering, setHovering] = useState(false)
  const isDark = mounted ? resolvedTheme === 'dark' : true

  useEffect(() => {
    setMounted(true)
  }, [])

  const ink = useMemo(() => {
    const id = color ?? (isDark ? 'creme' : 'encre')
    return COLOR_BY_ID.get(id)?.hex ?? (isDark ? '#f1efe9' : ENCRE)
  }, [color, isDark])

  const paper =
    paperProp ??
    (mounted
      ? readCssColor('--background', isDark ? '#0a0a0c' : '#ffffff')
      : isDark
        ? '#0a0a0c'
        : '#ffffff')

  const resolvedShape =
    resolveShapeId(shape) ?? (SHAPE_BY_ID.has(shape) ? shape : DEFAULT_SHAPE)
  // Trio is a multi-body mark — single React avatar falls back to the star.
  const engineShapeId = isClusterShape(resolvedShape) ? DEFAULT_SHAPE : resolvedShape
  const shapeRadii = SHAPE_BY_ID.get(engineShapeId)?.radii ?? null
  const shapeRing = !!SHAPE_BY_ID.get(engineShapeId)?.ring
  const expressionDef = expression ? EXPRESSION_BY_ID.get(expression) ?? null : null
  const liveState: StateId =
    hovering && hoverState && frozenAt == null ? hoverState : state

  const engineRef = useRef<BotEngine | null>(null)
  const clockRef = useRef(0)
  const shapeRef = useRef(engineShapeId)
  const containerRef = useRef<HTMLDivElement | null>(null)
  shapeRef.current = engineShapeId

  if (!engineRef.current) {
    engineRef.current = new BotEngine(R, liveState, shapeRadii, expressionDef)
  }

  const [frame, setFrame] = useState<BotFrame>(() =>
    decorate(engineRef.current!, engineRef.current!.sample(frozenAt ?? 0), frozenAt ?? 0, engineShapeId)
  )

  useEffect(() => {
    engineRef.current!.setShape(shapeRadii, clockRef.current)
  }, [shapeRadii])

  useEffect(() => {
    const engine = engineRef.current!
    if (engine.state !== liveState) {
      engine.setState(liveState, clockRef.current)
    }
  }, [liveState])

  useEffect(() => {
    engineRef.current!.setExpression(expressionDef, clockRef.current)
  }, [expressionDef])

  useEffect(() => {
    const engine = engineRef.current!
    if (frozenAt != null) {
      setFrame(decorate(engine, engine.sample(frozenAt), frozenAt, shapeRef.current))
      return
    }

    let raf = 0
    let last = performance.now()
    let reduced = false
    let alive = true

    const media =
      typeof window !== 'undefined'
        ? window.matchMedia('(prefers-reduced-motion: reduce)')
        : null
    const syncReduced = () => {
      reduced = media?.matches ?? false
    }
    syncReduced()
    media?.addEventListener('change', syncReduced)

    const tick = (nowMs: number) => {
      if (!alive) return
      const dt = Math.min(0.05, (nowMs - last) / 1000)
      last = nowMs
      clockRef.current += reduced ? dt * 0.15 : dt
      const clock = clockRef.current
      // rAF setState is fine alone; never call it after unmount, and never
      // re-enter while React is already tearing this tree down (max-depth #185).
      setFrame(decorate(engine, engine.sample(clock), clock, shapeRef.current))
      raf = requestAnimationFrame(tick)
    }

    raf = requestAnimationFrame(tick)
    return () => {
      alive = false
      cancelAnimationFrame(raf)
      media?.removeEventListener('change', syncReduced)
    }
  }, [frozenAt])

  // Eyes track the mouse pointer via the engine's `Look` input. The pointer is
  // measured relative to the avatar centre; distance drives `mix` (how strongly
  // the gaze commits) and `wander` fades out while the cursor is present.
  useEffect(() => {
    if (!followCursor) return
    const engine = engineRef.current!

    const media =
      typeof window !== 'undefined'
        ? window.matchMedia('(prefers-reduced-motion: reduce)')
        : null
    if (media?.matches) return

    const onMove = (e: PointerEvent) => {
      const el = containerRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      if (rect.width === 0 || rect.height === 0) return
      const cx = rect.left + rect.width / 2
      const cy = rect.top + rect.height / 2
      // Normalise pointer offset by a soft radius so far-away motion saturates.
      const reach = Math.max(rect.width, rect.height) * 6
      const nx = (e.clientX - cx) / reach
      const ny = (e.clientY - cy) / reach
      const dist = Math.min(1, Math.hypot(nx, ny))
      // Absolute look direction, capped so the eyes stay in the star pocket.
      const yaw = Math.max(-26, Math.min(26, nx * 90))
      const pitch = Math.max(-22, Math.min(22, -ny * 70))
      const mix = 0.35 + 0.65 * dist
      engine.setLook(
        { yaw, pitch, mix, spin: 0, wander: 1 - mix },
        clockRef.current
      )
    }

    const onLeave = () => {
      // Keep the last turn but let automatic drift return.
      engine.setLook({ yaw: 0, pitch: 0, mix: 0, spin: 0, wander: 1 }, clockRef.current)
    }

    window.addEventListener('pointermove', onMove, { passive: true })
    window.addEventListener('pointerleave', onLeave)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerleave', onLeave)
      engine.setLook(null, clockRef.current)
    }
  }, [followCursor])

  const darkBody = ink === ENCRE || ink.toLowerCase() === '#0a0a0c'
  // Body diameter is 2R inside a 2·VB square — scale the SVG so the blob fills `size`.
  const svgSize = fitBody ? size * (VB / R) : size
  // Dark body on a coloured chat page: cream trail stops read as a pale pad.
  // Remap every arc (comet, connect, agent…) to ink mixes — not only `cm*`.
  const inkTrail = [mixHex(paper, ink, 0.42), mixHex(paper, ink, 0.7), ink] as const
  const arcs = darkBody
    ? frame.arcs.map((arc) => ({
        ...arc,
        grad: { ...arc.grad, stops: [...inkTrail] }
      }))
    : frame.arcs

  const ringInk = mixHex(ink, paper, 0.42)
  const planetRing = (() => {
    const g = frame.bodyGaze
    const o = frame.bodyOffset
    const tilt = Math.min(1, Math.hypot(g.yaw, g.pitch) / 55)
    const squash = 1 - 0.05 * tilt
    return {
      rot: 18 + g.yaw * 0.05 + g.pitch * 0.02,
      cy: 0.06 * R + Math.sin((-g.pitch * Math.PI) / 180) * R * 0.03,
      ox: o.x + Math.sin((g.yaw * Math.PI) / 180) * R * 0.02,
      oy: o.y,
      rx: 1.46 * R * squash,
      ry: 0.28 * R * (1.15 + 0.85 * (1 - squash))
    }
  })()

  const onHoverEnter = () => {
    if (hoverState) setHovering(true)
  }
  const onHoverLeave = () => {
    if (hoverState) setHovering(false)
  }

  const dotNodes = (dots: BotFrame['dots'], keyPrefix: string) =>
    dots.map((dot, i) => {
      const body = ink
      const fill =
        dot.color ??
        (dot.accent
          ? decorAccent(body)
          : dot.depth === undefined
            ? body
            : mixHex(paper, body, dot.depth))
      const sparkle = dot.d === LOIS_SPARKLE_SM
      const pathScale = sparkle ? (dot.pathScale ?? 0.075) : (dot.pathScale ?? 1)
      if (dot.d) {
        return (
          <path
            key={`${keyPrefix}${i}`}
            d={dot.d}
            fill={fill}
            opacity={dot.opacity}
            transform={`translate(${dot.x} ${dot.y}) rotate(${dot.rot ?? 0}) scale(${pathScale * R})`}
          />
        )
      }
      return (
        <circle
          key={`${keyPrefix}${i}`}
          cx={dot.x}
          cy={dot.y}
          r={dot.r}
          fill={fill}
          opacity={dot.opacity}
        />
      )
    })

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center overflow-visible',
        (onClick || hoverState) && 'cursor-pointer',
        className
      )}
      style={{ width: size, height: size, ...style }}
      aria-hidden={onClick ? undefined : true}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      title={title}
      onClick={onClick}
      onMouseEnter={onHoverEnter}
      onMouseLeave={onHoverLeave}
      onFocus={onHoverEnter}
      onBlur={onHoverLeave}
      onKeyDown={
        onClick
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                onClick(event as unknown as ReactMouseEvent<HTMLElement>)
              }
            }
          : undefined
      }
    >
      <svg
        width={svgSize}
        height={svgSize}
        viewBox={`${-VB} ${-VB} ${VB * 2} ${VB * 2}`}
        className={cn('overflow-visible', fitBody && 'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2')}
      >
        <defs>
          <mask
            id={maskId}
            maskUnits="userSpaceOnUse"
            x={-VB}
            y={-VB}
            width={VB * 2}
            height={VB * 2}
          >
            <path d={frame.bodyPath} fill="#fff" />
            {frame.eyes.map((eye, i) => (
              <path
                key={i}
                d={eye.d}
                transform={eye.matrix}
                opacity={eye.alpha}
                fill="#000"
              />
            ))}
            {frame.notch ? (
              <circle
                cx={frame.notch.x}
                cy={frame.notch.y}
                r={frame.notch.r}
                fill="#000"
              />
            ) : null}
          </mask>
          {frame.volumeSheen ? (
            <>
              <clipPath id={sheenClipId}>
                <path d={frame.bodyPath} />
              </clipPath>
              <radialGradient
                id={sheenGradId}
                gradientUnits="userSpaceOnUse"
                cx={frame.volumeSheen.cx}
                cy={frame.volumeSheen.cy}
                r={Math.max(frame.volumeSheen.rx, frame.volumeSheen.ry)}
                fx={frame.volumeSheen.cx}
                fy={frame.volumeSheen.cy}
              >
                <stop offset="0%" stopColor={mixHex(ink, paper, 0.18)} stopOpacity={1} />
                <stop offset="45%" stopColor={mixHex(ink, paper, 0.18)} stopOpacity={0.4} />
                <stop offset="100%" stopColor={mixHex(ink, paper, 0.18)} stopOpacity={0} />
              </radialGradient>
            </>
          ) : null}
          {arcs.map((arc) => (
            <linearGradient
              key={arc.id}
              id={`${reactId}-${arc.id}`}
              gradientUnits="userSpaceOnUse"
              x1={arc.grad.x1}
              y1={arc.grad.y1}
              x2={arc.grad.x2}
              y2={arc.grad.y2}
            >
              {arc.grad.stops.map((c, i) => (
                <stop
                  key={i}
                  offset={i / Math.max(1, arc.grad.stops.length - 1)}
                  stopColor={c}
                />
              ))}
            </linearGradient>
          ))}
          {auraBorder ? (
            <>
              <linearGradient
                id={`${reactId}-aura`}
                gradientUnits="userSpaceOnUse"
                x1={-R}
                y1={-R}
                x2={R}
                y2={R}
              >
                <stop offset="0%" stopColor="var(--orb-color-from, #34d399)" />
                <stop offset="55%" stopColor="var(--orb-color-to, #60a5fa)" />
                <stop offset="100%" stopColor="var(--orb-color-from, #34d399)" />
              </linearGradient>
              <filter
                id={`${reactId}-aura-blur`}
                x="-40%"
                y="-40%"
                width="180%"
                height="180%"
                colorInterpolationFilters="sRGB"
              >
                <feGaussianBlur
                  in="SourceGraphic"
                  stdDeviation="6"
                  result="blur"
                />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </>
          ) : null}
        </defs>

        <g fill="none" strokeLinecap="round">
          {arcs.map((arc) => (
            <path
              key={`b${arc.id}`}
              d={arc.back}
              stroke={`url(#${reactId}-${arc.id})`}
              strokeWidth={arc.width}
              opacity={arc.opacity}
            />
          ))}
        </g>

        {frame.dotsBehind ? <g>{dotNodes(frame.dots, 'pb')}</g> : null}

        {shapeRing ? (
          <ellipse
            cx={0}
            cy={planetRing.cy}
            rx={planetRing.rx}
            ry={planetRing.ry}
            fill="none"
            stroke={ringInk}
            strokeWidth={0.1 * R}
            strokeLinecap="round"
            transform={`translate(${planetRing.ox} ${planetRing.oy}) rotate(${planetRing.rot})`}
            opacity={frame.bodyAlpha}
          />
        ) : null}

        {frame.universe ? (
          <g
            fill="none"
            stroke={ink}
            strokeLinecap="round"
            opacity={frame.universe.ring.opacity}
            transform={`translate(${frame.universe.ring.cx ?? 0} ${frame.universe.ring.cy ?? 0})`}
          >
            <ellipse
              cx={0}
              cy={0}
              rx={frame.universe.ring.rx}
              ry={frame.universe.ring.ry}
              strokeWidth={frame.universe.ring.stroke}
              transform={`rotate(${frame.universe.ring.rot})`}
            />
          </g>
        ) : null}

        {(frame.universe?.trails ?? [])
          .filter((trail) => trail.behind)
          .map((trail, i) => (
            <g
              key={`trail-b${i}`}
              opacity={trail.opacity}
              transform={`translate(${trail.x} ${trail.y}) scale(${trail.scale})`}
            >
              <path d={LOIS_SPARKLE_SM} fill={ink} />
            </g>
          ))}

        {frame.universe?.satellite.behind ? (
          <g
            opacity={frame.universe.satellite.opacity}
            transform={`translate(${frame.universe.satellite.x} ${frame.universe.satellite.y}) scale(${frame.universe.satellite.scale})`}
          >
            <path d={LOIS_SPARKLE_SM} fill={ink} />
          </g>
        ) : null}

        {auraBorder ? (
          <g
            aria-hidden
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            filter={`url(#${reactId}-aura-blur)`}
            opacity={frame.bodyAlpha}
          >
            {/* Stroke widths in viewBox units; --orb-* set by useOrbLevel. */}
            <path
              d={frame.bodyPath}
              stroke={`url(#${reactId}-aura)`}
              style={{
                strokeWidth: 'calc(8 + 16 * var(--orb-level, 0))',
                opacity: 'calc(0.2 + 0.75 * var(--orb-level, 0))'
              }}
            />
            <path
              d={frame.bodyPath}
              stroke={`url(#${reactId}-aura)`}
              style={{
                strokeWidth: 'calc(2.5 + 6 * var(--orb-treble, var(--orb-level, 0)))',
                opacity: 'calc(0.35 + 0.55 * var(--orb-mid, var(--orb-level, 0)))'
              }}
            />
          </g>
        ) : null}

        <g opacity={frame.bodyAlpha}>
          <path d={frame.bodyPath} fill={paper} />
          <g mask={`url(#${maskId})`}>
            <path d={frame.bodyPath} fill={ink} />
            {frame.volumeSheen ? (
              <g clipPath={`url(#${sheenClipId})`}>
                <ellipse
                  cx={frame.volumeSheen.cx}
                  cy={frame.volumeSheen.cy}
                  rx={frame.volumeSheen.rx}
                  ry={frame.volumeSheen.ry}
                  fill={`url(#${sheenGradId})`}
                  opacity={frame.volumeSheen.opacity}
                />
              </g>
            ) : null}
          </g>
        </g>

        {shapeRing ? (
          <g
            transform={`translate(${planetRing.ox} ${planetRing.oy}) rotate(${planetRing.rot})`}
            clipPath={`url(#${planetRingFrontId})`}
            opacity={frame.bodyAlpha}
          >
            <defs>
              <clipPath id={planetRingFrontId}>
                <rect x={-2.5 * R} y={0} width={5 * R} height={2.5 * R} />
              </clipPath>
            </defs>
            <ellipse
              cx={0}
              cy={planetRing.cy}
              rx={planetRing.rx}
              ry={planetRing.ry}
              fill="none"
              stroke={ringInk}
              strokeWidth={0.1 * R}
              strokeLinecap="round"
            />
          </g>
        ) : null}

        {(frame.universe?.trails ?? [])
          .filter((trail) => !trail.behind)
          .map((trail, i) => (
            <g
              key={`trail-f${i}`}
              opacity={trail.opacity}
              transform={`translate(${trail.x} ${trail.y}) scale(${trail.scale})`}
            >
              <path d={LOIS_SPARKLE_SM} fill={ink} />
            </g>
          ))}

        {frame.universe && !frame.universe.satellite.behind ? (
          <g
            opacity={frame.universe.satellite.opacity}
            transform={`translate(${frame.universe.satellite.x} ${frame.universe.satellite.y}) scale(${frame.universe.satellite.scale})`}
          >
            <path d={LOIS_SPARKLE_SM} fill={ink} />
          </g>
        ) : null}

        {!frame.dotsBehind ? <g>{dotNodes(frame.dots, 'pf')}</g> : null}

        {frame.notif ? (
          <circle
            cx={frame.notif.x}
            cy={frame.notif.y}
            r={frame.notif.r}
            fill={NOTIF_BLUE}
          />
        ) : null}

        <g fill="none" strokeLinecap="round">
          {arcs.map((arc) => (
            <path
              key={`f${arc.id}`}
              d={arc.front}
              stroke={`url(#${reactId}-${arc.id})`}
              strokeWidth={arc.width}
              opacity={arc.opacity}
            />
          ))}
        </g>
      </svg>
    </div>
  )
}
